import { Component, OnInit, ViewChild } from '@angular/core';
import { KeywordExtractionService } from '@services/keyword-extraction.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FunctionCategory } from '@models/functionCategory.model';
import { KeywordsSelected } from '@models/keywordsSelected.model';
import { ExtractedKeywordListComponent } from './extracted-keyword-list/extracted-keyword-list.component';
import { PredefinedKeyword } from '@models/predefinedKeyword.model';
import { AcknowledgeMessage } from '../../../../shared/_constants';
import { KeywordType, StudySearchStrategyProcess } from 'src/app/mystudies/_study_constants';

@Component({
  selector: 'app-add-keywords-dialog',
  templateUrl: './add-keywords-dialog.component.html',
  styleUrls: ['./add-keywords-dialog.component.css'],
})
export class AddKeywordsDialogComponent implements OnInit {
  @ViewChild('keywordList', { static: false }) keywordList: ExtractedKeywordListComponent;

  public readOnly: boolean;
  public keywordTypeId: KeywordType;
  public keywordExtractionService: KeywordExtractionService;

  showLoader = false;
  listName = '';
  keywordInput: string;
  keywordInvalid = false;
  predefinedList: any = {};
  selectedPredefinedCount: number;
  selectedExtractedCount: number;

  private _keywordExtractionService: KeywordExtractionService;
  private keywordsChanged: any[] = [];
  private selectedPredefined: PredefinedKeyword[] = [];
  private removedPredefined: KeywordsSelected[] = [];
  extractionInProgressMsg = AcknowledgeMessage.ExtractKeywordsInProgress;

  constructor(private activeModal: NgbActiveModal) {}

  ngOnInit() {
    if (this.keywordTypeId === KeywordType.IncludeProduct) {
      this.listName = 'Product ';
    } else if (this.keywordTypeId === KeywordType.IncludeFunction) {
      this.listName = 'Function ';
    } else if (this.keywordTypeId === KeywordType.Exclude) {
      this.listName = 'Exclude ';
    }
    // Create a local copy so that changes are not made unless saved
    this._keywordExtractionService = Object.assign({}, this.keywordExtractionService);
    this.initializePredefinedList();
    this.selectedExtractedCount = this._keywordExtractionService.selectedKeywords.filter(
      (k) => k.keywordTypeId === this.keywordTypeId && k.keywordsExtractedId && k.isActive
    ).length;
  }

  async addCustomKeyword() {
    this.keywordInvalid = false;
    if (!this.keywordInput) {
      this.keywordInvalid = true;
      return;
    }

    try {
      this.showLoader = true;
      // Add Keyphrase
      await this.keywordExtractionService.addCustomKeyword(this.keywordInput);
      this.keywordList.reset();
      this.keywordInput = '';
    } finally {
      this.showLoader = false;
    }
  }

  get extractionInProgress() {
    return this.keywordExtractionService.keywordExtractionStatus === StudySearchStrategyProcess.InProgress;
  }

  selectChanged(keyword: PredefinedKeyword) {
    if (keyword.selected) {
      this.addSelectedKeywordLocal(keyword);
    } else {
      this.removeselectedKeywordLocal(keyword);
    }
  }

  selectAll(category: FunctionCategory) {
    // category.keywordTypeId = this.keywordTypeId;
    this.predefinedList[category.functionCategoryId].forEach((k) => {
      this.addSelectedKeywordLocal(k, true);
      k.selected = true;
    });
  }

  close() {
    this.activeModal.close();
  }

  addToList() {
    try {
      this.showLoader = true;
      // this.keywordExtractionService.functionCategories = this._keywordExtractionService.functionCategories;
      // this.keywordsAdded.forEach(k => this.keywordExtractionService.addCustomKeyword(k));

      // Update the Extracted Keywords
      this.keywordsChanged.forEach((k) => {
        if (k.type) {
          this.keywordExtractionService.selectKeyword(k.keyword, k.type);
        } else {
          this.keywordExtractionService.removeKeyword(
            this.keywordExtractionService.selectedKeywords.find((kw) => kw.keywordsExtractedId === k.keyword.keywordsExtractedId)
          );
        }
      });

      // Update the Predefined Keywords
      this.removedPredefined.forEach((k) => this.keywordExtractionService.removeKeyword(k));
      this.selectedPredefined.forEach((k) => this.keywordExtractionService.addPredefinedKeyword(k.functionKeyword, this.keywordTypeId));

      this.activeModal.close();
    } finally {
      this.showLoader = false;
    }
  }

  onKeywordSelected(selectedKeyword: any) {
    const keywordChanged = this.keywordsChanged.find((kc) => kc.keyword.keywordsExtractedId === selectedKeyword.keyword.keywordsExtractedId);

    if (keywordChanged) {
      if (keywordChanged.type !== this.keywordTypeId && selectedKeyword.type === this.keywordTypeId) {
        this.selectedExtractedCount += 1;
      } else if (keywordChanged.type === this.keywordTypeId && selectedKeyword.type !== this.keywordTypeId) {
        this.selectedExtractedCount -= 1;
      }
      keywordChanged.type = selectedKeyword.type;
    } else {
      this.keywordsChanged.push(selectedKeyword);

      const existingSelectedKeyword = this._keywordExtractionService.selectedKeywords.find(
        (k) => k.keywordsExtractedId === selectedKeyword.keyword.keywordsExtractedId
      );
      if (existingSelectedKeyword) {
        existingSelectedKeyword.isActive = true;
        if (existingSelectedKeyword.keywordTypeId !== this.keywordTypeId && selectedKeyword.type === this.keywordTypeId) {
          this.selectedExtractedCount += 1;
        } else if (existingSelectedKeyword.keywordTypeId === this.keywordTypeId && selectedKeyword.type !== this.keywordTypeId) {
          this.selectedExtractedCount -= 1;
        }
      } else if (selectedKeyword.type === this.keywordTypeId) {
        this.selectedExtractedCount += 1;
      }
    }
  }

  private initializePredefinedList() {
    this.keywordExtractionService.functionCategories.forEach((c) => {
      this.predefinedList[c.functionCategoryId] = c.functionKeywords.map(
        (k) =>
          <PredefinedKeyword>{
            keyword: k.keyword,
            functionKeywordId: k.functionKeywordId,
            functionKeyword: k,
            selected: this.keywordExtractionService.selectedKeywords.find((sk) => sk.functionKeywordId === k.functionKeywordId && sk.isActive)
              ? true
              : false,
            disabled: this.keywordExtractionService.selectedKeywords.find(
              (sk) => sk.functionKeywordId === k.functionKeywordId && sk.keywordTypeId !== this.keywordTypeId && sk.isActive
            )
              ? true
              : false,
          }
      );
    });

    this.selectedPredefinedCount = this._keywordExtractionService.selectedKeywords.filter(
      (k) => k.keywordTypeId === this.keywordTypeId && k.functionKeywordId && k.isActive
    ).length;
  }

  private addSelectedKeywordLocal(keyword: PredefinedKeyword, incrementedNotSelectedOnly = false) {
    // Add if it has not been added locally in the dialog or was not previously selected
    if (
      !this.selectedPredefined.find((sk) => keyword.functionKeywordId === sk.functionKeywordId) &&
      !this._keywordExtractionService.selectedKeywords.find((k) => keyword.functionKeywordId === k.functionKeywordId && k.isActive)
    ) {
      this.selectedPredefined.push(keyword);
      if (!incrementedNotSelectedOnly || !keyword.selected) {
        this.selectedPredefinedCount += 1;
      }
    }

    // Return if it had been removed locally in the dialog
    this.removedPredefined = this.removedPredefined.filter((k) => k.functionKeywordId !== keyword.functionKeywordId);
  }

  private removeselectedKeywordLocal(keyword: PredefinedKeyword) {
    // Remove if it has been added locally in the dialog
    this.selectedPredefined = this.selectedPredefined.filter((k) => k.functionKeywordId !== keyword.functionKeywordId);

    // Remove if previously selected
    const removedKeyword = this._keywordExtractionService.selectedKeywords.find(
      (k) => k.functionKeywordId === keyword.functionKeywordId && k.isActive
    );
    if (removedKeyword) {
      this.removedPredefined.push(removedKeyword);
    }
    this.selectedPredefinedCount -= 1;
  }
}
